import { createContext, useContext, useEffect, useState } from "react";

import * as Sentry from "@sentry/browser";
import { fetchAuthSession } from "aws-amplify/auth";

import { CenteredLoader } from "app/components/authentication/CenteredLoader";
import { listenToAuth } from "app/utils/authHub";

/** One entry of Cognito's `identities` claim: the user as a federated IdP knows them. */
export interface Identity {
  userId: string;
  [key: string]: string;
}

/**
 * The profile claims the console reads from the Cognito ID token. Amplify v5
 * handed these over as `user.attributes`; v6's `AuthUser` carries only the
 * username and sub, and the token already holds the rest, so no GetUser call.
 */
export interface AuthClaims {
  name?: string;
  email?: string;
  /** The first federated identity, for users who signed in through SSO. */
  identity?: Identity;
}

const AuthClaimsContext = createContext<AuthClaims>({});

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

// In the ID token the claim is already an array; from the user attributes it
// was a JSON string. Accept both so the shape of the token cannot bite us.
function parseIdentity(raw: unknown): Identity | undefined {
  let list = raw;
  if (typeof raw === "string") {
    try {
      list = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (!Array.isArray(list)) {
    return undefined;
  }
  const first: unknown = list[0];
  if (
    first &&
    typeof first === "object" &&
    typeof (first as { userId?: unknown }).userId === "string"
  ) {
    return first as Identity;
  }
  return undefined;
}

/** Picks the claims the console uses out of an ID token payload. */
export function parseAuthClaims(payload: Record<string, unknown>): AuthClaims {
  return {
    name: asString(payload.name),
    email: asString(payload.email),
    identity: parseIdentity(payload.identities),
  };
}

/** Reads the claims from the current session's ID token; empty when signed out. */
export async function fetchAuthClaims(): Promise<AuthClaims> {
  const { tokens } = await fetchAuthSession();
  return parseAuthClaims(tokens?.idToken?.payload ?? {});
}

/** Props for AuthClaimsProvider */
interface Props {
  /** Rendered once the claims have been read. */
  children: React.ReactNode;
}

/**
 * Loads the ID token claims once the user is signed in, and holds the shell
 * back until they're in hand: consumers read them synchronously, so nothing
 * downstream (Segment's `identify`, the user menu) sees a user without them
 * first. Re-read on `tokenRefresh`, which is when a changed attribute would
 * reach the token.
 */
export function AuthClaimsProvider({ children }: Props) {
  const [claims, setClaims] = useState<AuthClaims | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const next = await fetchAuthClaims();
        if (!cancelled) setClaims(next);
      } catch (err) {
        if (cancelled) return;
        // The shell can still render; the menu falls back to the username.
        Sentry.captureException(err);
        setClaims({});
      }
    }

    load();
    const unsubscribe = listenToAuth(({ payload }) => {
      if (payload.event === "tokenRefresh") {
        load();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  if (claims === null) {
    return <CenteredLoader />;
  }

  return (
    <AuthClaimsContext.Provider value={claims}>
      {children}
    </AuthClaimsContext.Provider>
  );
}

/** The signed-in user's ID token claims; empty outside the provider (local dev). */
export function useAuthClaims(): AuthClaims {
  return useContext(AuthClaimsContext);
}
